import React, { useState, useRef, useEffect } from 'react';
import { useAudio } from './hooks/useAudio';
import { useChat } from './hooks/useChat';
import { ImageModal } from './components/ImageModal';
import { MessageItem } from './components/MessageItem';
import { ChatInput } from './components/ChatInput';
import { searchAPI } from './services/api';
import { useSpeechRecognition } from './hooks/useSpeechRecognition';
import { UploadModal } from './components/Upload';
const Main = () => {
    const { conversationHistory, setConversationHistory, messages, setMessages } = useChat();
    const [inputText, setInputText] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [enlargedImage, setEnlargedImage] = useState(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [userName, setUserName] = useState('');
    const { 
        isRecording, 
        transcription, 
        startRecording, 
        stopRecording 
    } = useSpeechRecognition();
    const { playAudio, stopAudio, isPlaying } = useAudio();
    const messagesEndRef = useRef(null);
    useEffect(() => {
        if (transcription) {
            setInputText(transcription);
        }
    }, [transcription]);
    useEffect(() => {  
        const user = JSON.parse(localStorage.getItem('userEmbeddings'));
        if (user) {
            setUserName(user.firstName + ' ' + user.lastName);
        }
    });
    useEffect(() => {
        const scrollToBottom = () => {
            messagesEndRef.current?.scrollIntoView({ 
                behavior: "smooth",
                block: "end"
            });
        };
        
        scrollToBottom();
    }, [messages]);
    const handleSearch = async (file, previewImage = null, userMessage = null) => {
        setIsSearching(true);
        
        if (!userMessage) {
            userMessage = {
                role: "user",
                content: inputText
            };
            setMessages(prev => [...prev, {
                id: Date.now(),
                text: inputText,
                sender: "user",
                timestamp: new Date()
            }]);
        }

        const updatedHistory = [...conversationHistory, userMessage];
        setConversationHistory(updatedHistory);

        if (previewImage) {
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                sender: "user",
                timestamp: new Date(),
                images: [previewImage]
            }]);
        }

        try {
            setInputText('');
            const requestBody = {
                query: {
                    type: file ? 'image' : 'text',
                    value: file ? file.name : inputText.trim()
                },
                messages: updatedHistory
            };
            stopRecording();
            const data = await searchAPI(requestBody, file);
            
            const assistantMessage = {
                role: "assistant",
                content: data.message
            };
            setConversationHistory(prev => [...prev, assistantMessage]);

            if (data.audioFileName) {
                playAudio(data.audioFileName);
            }

            setMessages(prev => [...prev, {
                id: Date.now() + 2,
                text: data.message,
                sender: "bot",
                timestamp: new Date()
            }]);

            if (data.results?.length > 0) {
                const processedGroups = data.results.map(group => ({
                    groupId: group.group_id,
                    similarity: group.group_similarity,
                    images: group.files.map(file => ({
                        src: `${process.env.REACT_APP_SERVER_URL}/api/image/${file.file_id}`, 
                        similarity: file.similarity
                    }))
                }));

                processedGroups.forEach((group, index) => {
                    setMessages(prev => [...prev, {
                        id: Date.now() + Math.random(),
                        sender: "bot",
                        timestamp: new Date(),
                        similarityText: `Rank ${index + 1} property: ${(group.similarity * 100).toFixed(1)}%`,
                        groupImages: group.images
                    }]);
                });
            }
        } catch (error) {
            console.error('Search error:', error);
            setMessages(prev => [...prev, {
                id: Date.now() + 2,
                text: "Sorry, there was an error processing your search. Please try again.",
                sender: "bot",
                timestamp: new Date()
            }]);
        }

        setIsSearching(false);
    };


    const handleToggleRecording = async () => {
        if (!isRecording) {
            await startRecording();
        } else {
            stopRecording();
        }
    };

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const file = files[0];
        const reader = new FileReader();
        
        reader.onloadend = () => {
            setMessages(prev => [...prev, {
                id: Date.now(),
                text: file.name,
                sender: "user",
                timestamp: new Date()
            }]);
            
            const userMessage = {
                role: "user",
                content: file.name
            };
            handleSearch(file, reader.result, userMessage);
        };
        
        reader.readAsDataURL(file);
    };

    return (
        <div className="chat-container">
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                marginRight: "16px",
                marginLeft: "16px",
                marginTop: "16px"
            }}>
                {/* Welcome message on the left */}
                <p style={{ fontSize: '20px', marginLeft: "30px" }}> Good day {userName && userName + "!"} </p>
                
                {/* Upload and Logout buttons on the right */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    alignItems: 'flex-end'
                }}>
                    <button 
                        onClick={() => setIsUploadModalOpen(true)}
                        style={{
                            background: '#22c55e',
                            color: 'white',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 6px -1px rgba(34, 197, 94, 0.2)',
                        }}
                        onMouseOver={(e) => {
                            e.target.style.background = '#16a34a';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 6px 8px -1px rgba(34, 197, 94, 0.25)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.background = '#22c55e';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 6px -1px rgba(34, 197, 94, 0.2)';
                        }}
                    >
                        Upload Images on the system
                    </button>
                    
                    <button 
                        onClick={() => {
                            // Add your logout logic here
                            localStorage.removeItem('authenticationToken');
                            window.location.href = '/';
                        }}
                        style={{
                            background: '#ef4444', 
                            color: 'white',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 4px -1px rgba(239, 68, 68, 0.2)',
                        }}
                        onMouseOver={(e) => {
                            e.target.style.background = '#dc2626';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 6px -1px rgba(239, 68, 68, 0.25)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.background = '#ef4444';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px -1px rgba(239, 68, 68, 0.2)';
                        }}
                    >
                        Logout
                    </button>
                </div>
            </div>
            <div className="messages-container">
    
                {messages.map((message) => (
                    <MessageItem
                        key={message.id}
                        message={message}
                        onImageClick={setEnlargedImage}
                    />
                ))}
                <div ref={messagesEndRef} />
            </div>
    
            <ChatInput
                inputText={inputText}
                setInputText={setInputText}
                isSearching={isSearching}
                onSend={() => handleSearch(null, null)}
                onImageUpload={handleImageUpload}
                isRecording={isRecording}
                onToggleRecording={handleToggleRecording}
            />
    
            <UploadModal 
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
            />
    
            {enlargedImage && (
                <ImageModal 
                    image={enlargedImage} 
                    onClose={() => setEnlargedImage(null)} 
                />
            )}
        </div>
    );
};

export default Main;